import { Button, makeStyles, Tab, Tabs, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import Container from "@saleor/components/Container";
import { useGetAvailableTemplatesQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { MallListDropOff_malls_edges_node } from "@saleor/dropOff/types/MallListDropOff";
import { dropOffListPath, dropOffSetupNewMallUrl } from "@saleor/dropOff/url";
import { getDropOffTabs } from "@saleor/dropOff/utils/handlers";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { renderCollection } from "@saleor/misc";
import { TemplateStatus } from "@saleor/types/globalTypes";
import { getQueryParamString } from "@saleor/utils/urls";
import classNames from "classnames";
import { parse as parseQs, stringify as stringifyQs } from "qs";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import urlJoin from "url-join";

import { TabValuesEnum } from "../../utils/filters";
import DropOffHomeLocLabels from "../DropOffHomeLocLabels/DropOffHomeLocLabels";
import DropOffTemplate from "../DropOffTemplate/DropOffTemplate";

interface DropOffTabsProps {
  mallData: MallListDropOff_malls_edges_node[];
  isOps?: boolean;
  disableAction: boolean;
}

const useStyles = makeStyles(
  theme => {
    const dataDisplay: React.CSSProperties = {
      flex: "60%",
      overflow: "auto",
      textAlign: "left"
    };

    return {
      [theme.breakpoints.down("md")]: {
        dataDisplay,
        dataGroup: {
          display: "flex"
        },
        dataTitle: {
          display: "block !important",
          flex: "40%",
          fontWeight: "bold",
          textAlign: "left"
        }
      }
    };
  },
  {
    name: "DropOffTabs"
  }
);

const DropOffTabs: React.FC<DropOffTabsProps> = props => {
  const { mallData, isOps, disableAction } = props;

  const mainClasses = useStyles(props);
  const classes = useTemplateStyles(props);
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();

  const tabs = getDropOffTabs();
  const { tab } =
    Object.keys(parseQs(location.search.substring(1))).length === 0
      ? { tab: TabValuesEnum.TEMPLATES }
      : parseQs(location.search.substring(1));
  const { mall } = parseQs(location.search.substring(1));

  const { data: templates } = useGetAvailableTemplatesQuery({
    displayLoader: true,
    variables: {
      filter: {
        mall: getQueryParamString(mall) ?? (mallData ? mallData[0]?.id : "")
      },
      first: 100
    }
  });

  const availableTemplates = templates?.templates?.edges.map(edge => edge.node);
  const nonDraftTemplates = availableTemplates?.filter(
    template => template.status !== TemplateStatus.DRAFT
  );

  const clickCreateNewTemplate = () => {
    if (nonDraftTemplates?.length >= 10) {
      notify({
        status: "error",
        text: "The maximum number of templates has been reached"
      });
    } else {
      navigate(
        dropOffSetupNewMallUrl({
          mall: mall ?? (mallData ? mallData[0]?.id : "")
        })
      );
    }
  };

  const [filterOutletStatus, setFilterOutletStatus] = React.useState(0);

  return (
    <Container>
      {renderCollection(mallData, (mall: MallListDropOff_malls_edges_node) => (
        <div key={"mall-" + mall?.id}>
          <div className={classes.headerSection}>
            <div className={classes.headerLeft}>
              <div className={classes.headerTitleMall}>
                <h5 className={classes.headerTitle}>
                  {intl.formatMessage(sectionNames.dropOffManagement)}
                </h5>
                <Typography color="primary" className={classes.mallTitle}>
                  {mall?.name}
                </Typography>
              </div>
              <div>
                {!isOps && (
                  <Button
                    size="large"
                    className={classes.btnTopViewAllMalls}
                    color="primary"
                    variant="outlined"
                    onClick={() => navigate(dropOffListPath)}
                  >
                    <FormattedMessage
                      defaultMessage="VIEW ALL MALLS"
                      description="button"
                    />
                  </Button>
                )}
              </div>
            </div>
            <div className={classes.headerLeft}>
              <Button
                size="large"
                className={classes.btnTopCreateNewTemplate}
                color="primary"
                variant="contained"
                disabled={templates?.templates?.edges == null || disableAction}
                onClick={() => clickCreateNewTemplate()}
              >
                <FormattedMessage
                  defaultMessage="Create new template"
                  description="button"
                />
              </Button>
            </div>
          </div>

          <Card className={classes.dropOffCard}>
            <>
              <Tabs
                className={classes.dropOffTabs}
                value={tab}
                scrollButtons="on"
                variant="scrollable"
                indicatorColor="primary"
                textColor="primary"
              >
                {tabs.map(({ label, route, showTab }) => (
                  <Tab
                    disableRipple
                    key={"tab-" + route}
                    value={route}
                    label={label}
                    onClick={() =>
                      navigate(
                        urlJoin(
                          dropOffListPath,
                          "?" + stringifyQs({ mall: mall.id, tab: route })
                        )
                      )
                    }
                    classes={{
                      root: mainClasses.tabRoot,
                      wrapper: classNames(mainClasses.tabLabel, {
                        [mainClasses.selectedTabLabel]: true
                      })
                    }}
                    style={{
                      display: !showTab ? "none" : "",
                      fontWeight: "bold"
                    }}
                  />
                ))}
              </Tabs>

              {tab === TabValuesEnum.TEMPLATES && mall ? (
                <>
                  <DropOffHomeLocLabels
                    filterOutletStatus={filterOutletStatus}
                    setFilterOutletStatus={setFilterOutletStatus}
                  />
                  <DropOffTemplate
                    mallId={mall?.id}
                    mall={mall}
                    disableAction={disableAction}
                    filterOutletStatus={filterOutletStatus}
                  />
                </>
              ) : (
                <Container className={classes.dropOffContainer}></Container>
              )}
            </>
          </Card>
        </div>
      ))}
    </Container>
  );
};

export default DropOffTabs;
